import "./LiensSectionPage.scss";

import Documents from "components/Documents/drawer/DocumentsDrawer";
import LoadingIcon from "components/Loading/LoadingIcon";
import { SECTION } from "constants/SettingsConstants";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import { useCallback, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { setCurrentSection, setShowTaskModal } from "reducers/main";
import { useGetLienByIdQuery } from "services/lien-api";

import { useLienFileData } from "./hooks/useLienFileData";
import { useLiensData } from "./hooks/useLiensData";
import { useLienSave } from "./hooks/useLienSave";
import LienDetailPage from "./lien-detail-page/LienDetailPage";
import LienPaymentHistory from "./lien-payments/LienPaymentHistory";
import { LienICDCodingDrawer } from "./LienICDCodingDrawer";
import LienPageContainer from "./LienPageContainer";
import { useDispatch, useSelector } from "utils/redux";
import { clearCurrentTask } from "reducers/files";
import TaskModal from "pages/tasks/components/modals/TaskModal";

const LiensSectionPage = () => {
  const dispatch = useDispatch();
  const { lienId } = useParams<{ lienId?: string }>();
  const prevLienIdRef = useRef(lienId);

  const showTaskModal = useSelector((state) => state.main.showTaskModal);

  const { data: lien, isFetching } = useGetLienByIdQuery(
    { lienId: lienId || "" },
    { skip: !lienId, refetchOnMountOrArgChange: true },
  );

  const handleClose = useCallback(() => {
    dispatch(clearCurrentTask());
    dispatch(setShowTaskModal(false));
  }, [dispatch]);

  const addTaskModal = useMemo(
    () => (
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleClose}
        fileId={String(lien?.file?.id)}
        serviceId={lien?.id}
        isLien={true}
      />
    ),
    [showTaskModal, handleClose, lienId, lien],
  );

  const { lienFileData } = useLienFileData();

  // load liens data for tree navigation - this ensures the navigation works on refresh
  useLiensData({
    fileId: lien?.file?.id?.toString(),
  });

  // show loading icon when we're fetching AND switching liens
  const isNavigating =
    (isFetching && prevLienIdRef.current !== lienId) ||
    (lienId && lien?.id.toString() !== lienId);

  useEffect(() => {
    prevLienIdRef.current = lienId;
  }, [lienId]);

  const handleSave = useLienSave(
    lienId,
    lien?.file?.claimants?.[0]?.id,
    lien?.file?.id,
  );

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.LIENS));
  }, [dispatch]);

  if (!lienId || !lien) {
    return null;
  }

  if (isNavigating) {
    return (
      <div className="flex jc-c ai-c h-100">
        <LoadingIcon />
      </div>
    );
  }

  return (
    <LienPageContainer>
      <LienDetailPage lien={lien} onSave={handleSave} />
      <LienPaymentHistory lien={lien} />
      <LienICDCodingDrawer lienId={lienId} />
      {addTaskModal}
      <Documents
        fileId={lien?.file?.id}
        serviceId={lien?.lien_file}
        serviceType="lien"
        additionalParams={{ lien_id: lien?.id?.toString() }}
      />
      <ClientInformationDrawer service={lienFileData} />
    </LienPageContainer>
  );
};

export default LiensSectionPage;
